import { lazy, Suspense, useEffect } from 'react';
import { Link, Outlet, useLocation } from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';
import { Button } from "@mui/material";
import { useShallow } from "zustand/react/shallow";

import Menu from "~/components/Menu";
import Loader from "~/components/Loader";
import Christmas from "~/components/Christmas";
import { globalStore } from "~/stores/global";
import { auth } from "~/providers/php-api";
import { myUseState } from "~/hooks/myUseState";
import { useMedia } from "~/hooks/useMedia";
import { getClassName, getInfoLine } from "~/utils/utils";
import { ASSETS } from "~/utils/assets";
import { ROUTES } from "~/const-files/const";
import MyButton from "~/my/MyButton";
import InfoLine from "../InfoLine";
import { getJWTFromCookies, setUserData } from "~/providers/user";
import Notifications from "../Notifications";
import { debugStore } from "~/stores/debug";
import DebugLine from "../DebugLine";
import SnowFlakes from "../Snowflakes";
import Halloween from "../Halloween";
import FirstDayToday from "../FirstDayToday";

import "./style.less";

// pixi je velka a pozadi je ve vychozim stavu vypnute
const PixiBackground = lazy(() => import("~/components/PixiBackground"));

interface IState {
	hamburgerVisible: boolean;
	menuVisible: boolean;
	pathname: string;
}

export default function Page() {
	const { pathname } = useLocation();
	const isSmallDevice = useMedia("(max-width: 680px)");
	const { state, setState, updateState } = myUseState<IState>({
		hamburgerVisible: false,
		menuVisible: true,
		pathname,
	});
	const { global, setHidePage, setChristmas, setLogAuthLoading } = globalStore(useShallow(globalState => ({
		global: globalState.global,
		setHidePage: globalState.setHidePage,
		setChristmas: globalState.setChristmas,
		setLogAuthLoading: globalState.setLogAuthLoading,
	})));
	const exportData = debugStore(debugState => debugState.debug.exportData);
	const infoLine = getInfoLine({
		messengerNews: global.messengerNews,
		uploadedFileNews: global.uploadedFileNews,
	});

	function onHamburgerClick() {
		setState(prev => ({
			...prev,
			menuVisible: !prev.menuVisible,
		}));
	}

	function onMenuClick() {
		isSmallDevice && updateState({
			menuVisible: false,
		});
	}

	function showPage() {
		setHidePage(false);
		setChristmas(false);
	}

	async function pageLoad() {
		const jwtCookie = getJWTFromCookies();

		if (jwtCookie) {
			setLogAuthLoading(true);

			const data = await auth();

			setUserData(data.data, !data.error && data.data?.logged === false);
		}
	}

	useEffect(() => {
		pageLoad();
	}, []);

	useEffect(() => {
		document.body.classList.toggle("winter-body", global.isWinter);
	}, [global.isWinter]);

	useEffect(() => {
		if (pathname !== state.pathname && state.hamburgerVisible && state.menuVisible) {
			updateState({
				menuVisible: false,
			});
		}

		updateState({
			pathname,
		});
	}, [pathname, state.pathname, state.hamburgerVisible, state.menuVisible]);

	useEffect(() => {
		updateState({
			hamburgerVisible: isSmallDevice,
			menuVisible: !isSmallDevice,
		});
	}, [isSmallDevice]);

	return <>
		<div className={getClassName(["mainContent", global.hidePage ? "hide-page" : "", global.isWinter ? "page-winter" : ""])}>
			{ global.isWinter && <SnowFlakes /> }
			<div className="mainContent__header">
				<div className="mainContent__headerTop">
					<div className="mainContent__headerTopPlaceholder mainContent__headerTopLeft">
						{ global.loginData?.isAdmin && <FirstDayToday variant="header" /> }
					</div>
					<div className="mainContent__headerCenter">
						<Link to={ROUTES.ROOT} className="mainContent__headerLogoLink" title="Úvodní stránka">
							<img className="mainContent__headerLogo" src={ASSETS.Logo} alt="Makuderovi" />
						</Link>
						<div className="mainContent__headerNames">
							<span className="mainContent__leftName">Roman</span>
							<span className="mainContent__separator">,</span>
							<span className="mainContent__otherName">Lenka</span>
							<span className="mainContent__separator">,</span>
							<span className="mainContent__otherName">Martin</span>
							<span className="mainContent__separator">&</span>
							<span className="mainContent__otherName">Tomáš</span>
						</div>
						<div className="mainContent__headerAnon">
							Makuderovi
						</div>
					</div>
					<div className="mainContent__headerTopPlaceholder">
						{ state.hamburgerVisible && <Button className="menu__hamburgetBtn" onClick={onHamburgerClick}>
							<MenuIcon sx={{ fill: "#000" }} />
						</Button> }
					</div>
				</div>
				{ state.menuVisible && <Menu onClick={onMenuClick} mobileVersion={state.hamburgerVisible} /> }
				{ infoLine.length > 0 && <InfoLine items={infoLine} /> }
				{ Object.keys(exportData).length > 0 && <DebugLine exportData={exportData} /> }
			</div>
			<div className="mainContent__cont">
				<div className="content">
					<Suspense fallback={<Loader />}>
						<Outlet />
					</Suspense>
				</div>
			</div>
		</div>
		<Notifications />
		{ global.pixiMode !== "none" && <Suspense fallback={null}>
			<PixiBackground mode={global.pixiMode} />
		</Suspense> }
		{ global.christmas && <Christmas /> }
		{ global.isHalloween && <Halloween /> }
		{ global.hidePage && <div className="mainContent__showPage">
			<MyButton text="Zobrazit stránku" onClick={showPage} />
		</div>}
	</>;
}
